import { EllipsisText, Flex, Text, type GridColDef } from '@shared/components';
import { formatDateTime } from '@shared/utils';

import { ExamLocationRequestStatusLabel } from '../exam-location-request-status-label';

import {
  type InReviewExamLocationRequest,
  type RejectedExamLocationRequest,
  type DraftExamLocationRequest,
} from './types';

export const draftColumns: GridColDef<DraftExamLocationRequest>[] = [
  {
    field: 'refCode',
    headerName: 'เลขที่คำขอ',
    width: 100,
    sortable: false,
    align: 'center',
    renderCell: ({ row }) => {
      return <Text>{row.refCode ?? '-'}</Text>;
    },
  },
  {
    field: 'province',
    headerName: 'จังหวัด',
    width: 144,
    sortable: false,
    renderCell: ({ row }) => {
      return <EllipsisText maxLines={1}>{row.province}</EllipsisText>;
    },
  },
  {
    field: 'locationName',
    headerName: 'สถานที่สอบ / อาคาร',
    flex: 1,
    minWidth: 200,
    sortable: false,
    renderCell: ({ row }) => {
      return (
        <Flex sx={{ flexDirection: 'column' }}>
          <EllipsisText maxLines={1} variant='body1Semibold'>
            {row.locationName}
          </EllipsisText>
          <EllipsisText maxLines={1}>อาคาร: {row.buildingName}</EllipsisText>
        </Flex>
      );
    },
  },
  {
    field: 'status',
    headerName: 'สถานะ',
    width: 160,
    sortable: false,
    renderCell: ({ row }) => {
      return <ExamLocationRequestStatusLabel variant={row.status} />;
    },
  },
  {
    field: 'editedBy',
    headerName: 'ผู้แก้ไขล่าสุด',
    width: 160,
    sortable: false,
    renderCell: ({ row }) => {
      return <EllipsisText maxLines={1}>{row.editedBy ?? '-'}</EllipsisText>;
    },
  },
  {
    field: 'editedAt',
    headerName: 'วันที่แก้ไขล่าสุด',
    width: 160,
    renderCell: ({ row }) => {
      return <Text>{formatDateTime(row.editedAt)}</Text>;
    },
  },
];

export const inReviewColumns: GridColDef<InReviewExamLocationRequest>[] = [
  {
    field: 'refCode',
    headerName: 'เลขที่คำขอ',
    width: 100,
    sortable: false,
    align: 'center',
    renderCell: ({ row }) => {
      return <Text>{row.refCode ?? '-'}</Text>;
    },
  },
  {
    field: 'province',
    headerName: 'จังหวัด',
    width: 144,
    sortable: false,
    renderCell: ({ row }) => {
      return <EllipsisText maxLines={1}>{row.province}</EllipsisText>;
    },
  },
  {
    field: 'locationName',
    headerName: 'สถานที่สอบ / อาคาร / ชั้น',
    flex: 1,
    minWidth: 200,
    sortable: false,
    renderCell: ({ row }) => {
      return (
        <Flex sx={{ flexDirection: 'column' }}>
          <EllipsisText maxLines={1} variant='body1Semibold'>
            {row.locationName}
          </EllipsisText>
          <EllipsisText maxLines={1}>
            อาคาร: {row.buildingName} ชั้น: {row.floor}
          </EllipsisText>
        </Flex>
      );
    },
  },
  {
    field: 'roomName',
    headerName: 'ห้องสอบ',
    flex: 1,
    minWidth: 200,
    sortable: false,
    renderCell: ({ row }) => {
      return (
        <Flex sx={{ flexDirection: 'column' }}>
          <EllipsisText maxLines={1}>ห้อง: {row.roomName}</EllipsisText>
          <EllipsisText maxLines={1}>เลขที่: {row.roomNumber}</EllipsisText>
        </Flex>
      );
    },
  },
  {
    field: 'status',
    headerName: 'สถานะ',
    width: 160,
    sortable: false,
    renderCell: ({ row }) => {
      return <ExamLocationRequestStatusLabel variant={row.status} />;
    },
  },
  {
    field: 'requestedAt',
    headerName: 'วันที่ส่งคำขอ',
    width: 160,
    renderCell: ({ row }) => {
      return <Text>{row.requestedAt ? formatDateTime(row.requestedAt) : '-'}</Text>;
    },
  },
];

export const rejectedColumns: GridColDef<RejectedExamLocationRequest>[] = [
  {
    field: 'refCode',
    headerName: 'เลขที่คำขอ',
    width: 100,
    sortable: false,
    align: 'center',
    renderCell: ({ row }) => {
      return <Text>{row.refCode ?? '-'}</Text>;
    },
  },
  {
    field: 'province',
    headerName: 'จังหวัด',
    width: 144,
    sortable: false,
    renderCell: ({ row }) => {
      return <EllipsisText maxLines={1}>{row.province}</EllipsisText>;
    },
  },
  {
    field: 'locationName',
    headerName: 'สถานที่สอบ / อาคาร / ชั้น',
    flex: 1,
    minWidth: 200,
    sortable: false,
    renderCell: ({ row }) => {
      return (
        <Flex sx={{ flexDirection: 'column' }}>
          <EllipsisText maxLines={1} variant='body1Semibold'>
            {row.locationName}
          </EllipsisText>
          <EllipsisText maxLines={1}>
            อาคาร: {row.buildingName} ชั้น: {row.floor}
          </EllipsisText>
        </Flex>
      );
    },
  },
  {
    field: 'roomName',
    headerName: 'ห้องสอบ',
    flex: 1,
    minWidth: 200,
    sortable: false,
    renderCell: ({ row }) => {
      return (
        <Flex sx={{ flexDirection: 'column' }}>
          <EllipsisText maxLines={1}>ห้อง: {row.roomName}</EllipsisText>
          <EllipsisText maxLines={1}>เลขที่: {row.roomNumber}</EllipsisText>
        </Flex>
      );
    },
  },
  {
    field: 'status',
    headerName: 'สถานะ',
    width: 160,
    sortable: false,
    renderCell: ({ row }) => {
      return <ExamLocationRequestStatusLabel variant={row.status} />;
    },
  },
  {
    field: 'rejectedAt',
    headerName: 'วันที่ไม่อนุมัติ',
    width: 160,
    renderCell: ({ row }) => {
      return <Text>{row.rejectedAt ? formatDateTime(row.rejectedAt) : '-'}</Text>;
    },
  },
];
